import { AppError } from '@app/errors/AppError'

import { isApiError } from '@app/packages/api/errors'
import type { ErrorResponse, ErrorResponseItem } from '@app/packages/api/v2/types/misc'

import { SerializableError } from './SerializableError'

/** extracts errors from error payload, and returns list of strings */
export function extractErrors(...errors: (Error | null | undefined)[]): string[] {
  return errors.flatMap(extractError).reduce<string[]>((c, e) => {
    if (!e) return c
    if (c.indexOf(e) === -1) c.push(e)
    return c
  }, [])
}

export const extractMetaErrors = (e: Error | undefined | null, label: string): string[] => {
  if (!e) return []
  if (!isApiError(e)) return []

  const err = e.response?.errors?.map(e => e.meta[label]).flatMap(x => (!x ? [] : Array.isArray(x) ? x.filter(x => !!x) : [x])) ?? []

  return err
}

export const extractMetaError = (e: Error | undefined | null, label: string): string | null => {
  const arr = extractMetaErrors(e, label)
  if (!arr.length) return null

  return arr.join(', ')
}

function detailOrTitle(e: ErrorResponseItem): string[] {
  // A detail that opens with its title already says it: «Привяжите карту: Привяжите карту: …» otherwise.
  const message = e.title && e.detail && !e.detail.startsWith(e.title) ? [e.title, e.detail].join(': ') : e.detail || e.title
  if (!message) return []
  const pointer = e.source?.pointer
  return pointer ? [`${formatPointer(pointer)}: ${message}`] : [message]
}

function formatPointer(pointer: string): string {
  return pointer
    .split('/')
    .filter(Boolean)
    .map(s => s.replace(/~1/g, '/').replace(/~0/g, '~'))
    .map((s, i) => (/^\d+$/.test(s) ? `[${s}]` : i === 0 ? s : `.${s}`))
    .join('')
}

/** extracts errors from error payload, and returns list of strings */
function extractError(error?: Error | null): string[] {
  if (!error) return []

  if (error instanceof SerializableError) return extractError((error.cause as any) ?? null)

  if (error instanceof AppError && error.localizedMessage) return [error.localizedMessage]
  const response: ErrorResponse = (error as any)?.response
  if (response) {
    const list = response.errors.flatMap(detailOrTitle)
    if (list.length) return list
  }

  return [error.message]
}
